import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Upload, Monitor, Play, Settings, Globe, Image, Video, MessageSquare, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOLD = '#A9B2B9';

function SignageModal({ isOpen, onClose }) {
  const [screens, setScreens] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('screens'); // screens or content
  const [uploading, setUploading] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [newScreenName, setNewScreenName] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [screensRes, contentRes] = await Promise.all([
        axios.get(`${API}/signage/screens`, { withCredentials: true }),
        axios.get(`${API}/signage/content`, { withCredentials: true })
      ]);
      setScreens(screensRes.data.screens || []);
      setContent(contentRes.data.content || []);
    } catch (error) {
      console.error('Failed to fetch signage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createScreen = async () => {
    if (!newScreenName.trim()) return;
    try {
      await axios.post(
        `${API}/signage/screens`,
        null,
        {
          params: { name: newScreenName },
          withCredentials: true
        }
      );
      setNewScreenName('');
      // Refetch to get the screen from database
      await fetchData();
      alert('Οθόνη δημιουργήθηκε επιτυχώς!');
    } catch (error) {
      console.error('Failed to create screen:', error);
      alert('Σφάλμα δημιουργίας οθόνης');
    }
  };

  const deleteScreen = async (screenId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την οθόνη;')) return;
    try {
      await axios.delete(`${API}/signage/screens/${screenId}`, { withCredentials: true });
      if (selectedScreen?.screen_id === screenId) {
        setSelectedScreen(null);
      }
      // Refetch to sync with database
      await fetchData();
      alert('Οθόνη διαγράφηκε');
    } catch (error) {
      console.error('Failed to delete screen:', error);
      alert('Σφάλμα διαγραφής οθόνης');
    }
  };

  const uploadContent = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/signage/content/upload?type=${type}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Refetch to get content from database
      await fetchData();
      alert('Το αρχείο ανέβηκε επιτυχώς!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Σφάλμα ανεβάσματος αρχείου');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteContent = async (contentId) => {
    if (!window.confirm('Διαγραφή αρχείου;')) return;
    try {
      await axios.delete(`${API}/signage/content/${contentId}`, { withCredentials: true });
      // Refetch to sync with database
      await fetchData();
    } catch (error) {
      console.error('Failed to delete content:', error);
    }
  };

  const updateScreenConfig = async (screenId, updates) => {
    try {
      // Parse updates properly for axios params
      const params = { ...updates };
      
      await axios.put(
        `${API}/signage/screens/${screenId}/config`,
        null,
        {
          params: params,
          withCredentials: true,
          paramsSerializer: params => {
            // Custom serializer to handle arrays properly
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                // Send array items individually
                value.forEach(item => searchParams.append(key, item));
              } else {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          }
        }
      );
      // DON'T refetch after every update - causes reload!
      // Only refetch when modal opens or after upload/delete
    } catch (error) {
      console.error('Failed to update config:', error);
      alert('Σφάλμα ενημέρωσης');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.95)' }}>
      <div className="w-full max-w-6xl bg-black border border-white/10 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Monitor className="w-6 h-6" style={{ color: GOLD }} />
            <h2 className="text-white text-lg font-light tracking-widest uppercase">Digital Signage</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('screens')}
            className={`px-6 py-3 text-xs tracking-widest uppercase transition-colors ${
              activeTab === 'screens'
                ? 'text-white border-b-2'
                : 'text-gray-500 hover:text-gray-300'
            }`}
            style={activeTab === 'screens' ? { borderColor: GOLD } : {}}
          >
            Διαχείριση Οθονών
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 text-xs tracking-widest uppercase transition-colors ${
              activeTab === 'content'
                ? 'text-white border-b-2'
                : 'text-gray-500 hover:text-gray-300'
            }`}
            style={activeTab === 'content' ? { borderColor: GOLD } : {}}
          >
            Διαχείριση Περιεχομένου
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Φόρτωση...</div>
          ) : activeTab === 'screens' ? (
            <ScreensTab
              screens={screens}
              content={content}
              newScreenName={newScreenName}
              setNewScreenName={setNewScreenName}
              createScreen={createScreen}
              deleteScreen={deleteScreen}
              updateScreenConfig={updateScreenConfig}
              selectedScreen={selectedScreen}
              setSelectedScreen={setSelectedScreen}
            />
          ) : (
            <ContentTab
              content={content}
              uploading={uploading}
              uploadContent={uploadContent}
              deleteContent={deleteContent}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ScreensTab({
  screens,
  content,
  newScreenName,
  setNewScreenName,
  createScreen,
  deleteScreen,
  updateScreenConfig,
  selectedScreen,
  setSelectedScreen
}) {
  return (
    <div className="space-y-6">
      {/* Create Screen */}
      <div className="border border-white/10 p-4">
        <h3 className="text-white text-xs font-light tracking-widest uppercase mb-3">Δημιουργία Νέας Οθόνης</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newScreenName}
            onChange={(e) => setNewScreenName(e.target.value)}
            placeholder="π.χ. Οθόνη Εισόδου"
            className="flex-1 bg-black border border-white/10 px-4 py-2 text-white text-sm focus:border-white/30 focus:outline-none"
            onKeyPress={(e) => e.key === 'Enter' && createScreen()}
          />
          <button
            onClick={createScreen}
            disabled={!newScreenName.trim()}
            className="px-6 py-2 text-xs tracking-widest uppercase transition-all disabled:opacity-30"
            style={{ background: GOLD, color: '#000' }}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Δημιουργία
          </button>
        </div>
      </div>

      {/* Screens List */}
      {screens.length === 0 ? (
        <div className="border border-white/5 py-10 text-center">
          <Monitor className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: GOLD }} />
          <p className="text-gray-500 text-xs">Δεν υπάρχουν οθόνες</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {screens.map((screen) => (
            <ScreenCard
              key={screen.screen_id}
              screen={screen}
              content={content}
              deleteScreen={deleteScreen}
              updateScreenConfig={updateScreenConfig}
              isSelected={selectedScreen?.screen_id === screen.screen_id}
              onSelect={() => setSelectedScreen(screen)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ScreenCard({ screen, content, deleteScreen, updateScreenConfig, isSelected, onSelect }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(5);
  const [transition, setTransition] = useState('fade');
  const [selectedContentIds, setSelectedContentIds] = useState([]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, [screen.screen_id]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/signage/screens/${screen.screen_id}/config`);
      setConfig(res.data.config);
      setDuration(res.data.config.settings?.duration || 5);
      setTransition(res.data.config.settings?.transition || 'fade');
      setSelectedContentIds(res.data.config.content_ids || []);
      setIsActive(res.data.screen.is_active !== false);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async () => {
    const newActive = !isActive;
    setIsActive(newActive);
    await updateScreenConfig(screen.screen_id, {
      is_active: newActive
    });
  };

  const updateSettings = async () => {
    await updateScreenConfig(screen.screen_id, {
      settings: JSON.stringify({ duration, transition })
    });
    alert('Ρυθμίσεις ενημερώθηκαν!');
  };

  const toggleContent = (contentId) => {
    // Just update local state - DON'T save to backend yet
    let newIds;
    if (selectedContentIds.includes(contentId)) {
      newIds = selectedContentIds.filter(id => id !== contentId);
    } else {
      newIds = [...selectedContentIds, contentId];
    }
    setSelectedContentIds(newIds);
  };

  const saveContentSelection = async () => {
    // Save all selected content to backend
    try {
      await updateScreenConfig(screen.screen_id, {
        content_ids: selectedContentIds
      });
      alert('✅ Επιλογές αποθηκεύτηκαν! Ο player θα ενημερωθεί αμέσως.');
    } catch (error) {
      alert('❌ Σφάλμα αποθήκευσης');
    }
  };

  const isOnline = screen.last_seen && 
    (new Date() - new Date(screen.last_seen)) < 30000;

  const playerUrl = `${BACKEND_URL}/signage?screen=${screen.screen_id}`;

  // Calculate stats
  const selectedContent = content.filter(c => selectedContentIds.includes(c.content_id));
  const videoCount = selectedContent.filter(c => c.type === 'video').length;
  const imageCount = selectedContent.filter(c => c.type === 'image').length;
  const totalSize = selectedContent.reduce((sum, c) => sum + c.size, 0);

  return (
    <div className="border border-white/10 p-4 hover:border-white/20 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-white text-sm font-light">{screen.name}</h3>
            <span
              className={`px-2 py-0.5 text-[10px] tracking-widest uppercase ${
                isOnline ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-500'
              }`}
            >
              {isOnline ? 'Online' : 'Offline'}
            </span>
            {/* ON/OFF Toggle */}
            <button
              onClick={toggleActive}
              className={`px-3 py-1 text-xs font-bold tracking-widest uppercase transition-all ${
                isActive 
                  ? 'bg-green-500 text-black hover:bg-green-400' 
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {isActive ? 'ON' : 'OFF'}
            </button>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-blue-400">
              🎥 {videoCount} videos
            </span>
            <span className="text-xs text-green-400">
              🖼️ {imageCount} images
            </span>
            <span className="text-xs text-gray-500">
              📦 {(totalSize / 1024 / 1024).toFixed(1)} MB
            </span>
          </div>
          
          <p className="text-gray-600 text-xs font-mono mb-2">{screen.screen_id}</p>
          <a
            href={playerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs mt-1 inline-block hover:underline"
            style={{ color: GOLD }}
          >
            <Play className="w-3 h-3 inline mr-1" />
            Άνοιγμα Player
          </a>
        </div>
        <button
          onClick={() => deleteScreen(screen.screen_id)}
          className="text-gray-600 hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="text-gray-600 text-xs py-4">Φόρτωση...</div>
      ) : config ? (
        <div className="space-y-4">
          {/* Ρυθμίσεις */}
          <div className="border border-white/10 p-3 bg-black/30">
            <label className="text-gray-500 text-[10px] uppercase tracking-widest mb-2 block">
              <Settings className="w-3 h-3 inline mr-1" />
              Ρυθμίσεις
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-gray-600 text-xs mb-1 block">Διάρκεια εικόνων (δευτ.)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  min="1"
                  max="60"
                  className="w-full bg-black border border-white/10 px-2 py-1 text-white text-sm focus:border-white/30 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-gray-600 text-xs mb-1 block">Transition</label>
                <select
                  value={transition}
                  onChange={(e) => setTransition(e.target.value)}
                  className="w-full bg-black border border-white/10 px-2 py-1 text-white text-sm focus:border-white/30 focus:outline-none"
                >
                  <option value="fade">Fade</option>
                  <option value="slide">Slide</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>
            <button
              onClick={updateSettings}
              className="mt-2 px-3 py-1 text-xs tracking-widest uppercase w-full"
              style={{ background: GOLD, color: '#000' }}
            >
              Αποθήκευση
            </button>
          </div>

          {/* Content Selection με λεπτομέρειες */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-500 text-[10px] uppercase tracking-widest">
                Περιεχόμενο Οθόνης ({selectedContent.length} επιλεγμένα)
              </label>
              <button
                onClick={saveContentSelection}
                className="px-3 py-1 text-xs font-bold tracking-widest uppercase"
                style={{ background: GOLD, color: '#000' }}
              >
                💾 ΑΠΟΘΗΚΕΥΣΗ
              </button>
            </div>
            {content.length === 0 ? (
              <p className="text-gray-600 text-xs">Δεν υπάρχει διαθέσιμο περιεχόμενο. Ανέβασε αρχεία πρώτα!</p>
            ) : (
              <div className="space-y-1 max-h-60 overflow-y-auto border border-white/5 rounded p-2">
                {content.map((item) => {
                  const isSelected = selectedContentIds.includes(item.content_id);
                  return (
                    <label
                      key={item.content_id}
                      className={`flex items-center gap-2 p-2 cursor-pointer rounded transition-all ${
                        isSelected 
                          ? 'bg-white/10 border border-white/20' 
                          : 'hover:bg-white/5 border border-transparent hover:border-white/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleContent(item.content_id)}
                        className="accent-[#A9B2B9]"
                      />
                      {item.type === 'video' ? (
                        <Video className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      ) : (
                        <Image className="w-4 h-4 text-green-400 flex-shrink-0" />
                      )}
                      <span className="text-white text-xs flex-1 truncate">{item.original_filename}</span>
                      <span className="text-gray-600 text-[10px] flex-shrink-0">
                        {(item.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-gray-500 text-[10px] mt-2">
              💡 Επίλεξε αρχεία και πάτα ΑΠΟΘΗΚΕΥΣΗ για να ενημερωθεί ο player live!
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ContentTab({ content, uploading, uploadContent, deleteContent }) {
  const videos = content.filter(c => c.type === 'video');
  const images = content.filter(c => c.type === 'image');

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="border border-white/10 p-4">
        <h3 className="text-white text-xs font-light tracking-widest uppercase mb-3">
          <Upload className="w-4 h-4 inline mr-2" />
          Ανέβασμα Αρχείων
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-500 text-xs mb-2">Videos</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => uploadContent(e, 'video')}
              disabled={uploading}
              className="hidden"
              id="video-upload"
            />
            <label
              htmlFor="video-upload"
              className={`flex items-center justify-center gap-2 px-4 py-3 border border-white/10 hover:bg-white/5 cursor-pointer transition-colors ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Video className="w-4 h-4 text-gray-500" />
              <span className="text-white text-xs">
                {uploading ? 'Ανέβασμα...' : 'Επιλογή Video'}
              </span>
            </label>
          </div>
          <div>
            <label className="block text-gray-500 text-xs mb-2">Images</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => uploadContent(e, 'image')}
              disabled={uploading}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`flex items-center justify-center gap-2 px-4 py-3 border border-white/10 hover:bg-white/5 cursor-pointer transition-colors ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Image className="w-4 h-4 text-gray-500" />
              <span className="text-white text-xs">
                {uploading ? 'Ανέβασμα...' : 'Επιλογή Εικόνα'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Videos List */}
      <div>
        <h3 className="text-white text-xs font-light tracking-widest uppercase mb-3">
          <Video className="w-4 h-4 inline mr-2" />
          Videos ({videos.length})
        </h3>
        {videos.length === 0 ? (
          <div className="border border-white/5 py-6 text-center">
            <p className="text-gray-500 text-xs">Δεν υπάρχουν videos</p>
          </div>
        ) : (
          <div className="grid gap-2">
            {videos.map((item) => (
              <ContentItem key={item.content_id} item={item} onDelete={deleteContent} />
            ))}
          </div>
        )}
      </div>

      {/* Images List */}
      <div>
        <h3 className="text-white text-xs font-light tracking-widest uppercase mb-3">
          <Image className="w-4 h-4 inline mr-2" />
          Images ({images.length})
        </h3>
        {images.length === 0 ? (
          <div className="border border-white/5 py-6 text-center">
            <p className="text-gray-500 text-xs">Δεν υπάρχουν εικόνες</p>
          </div>
        ) : (
          <div className="grid gap-2">
            {images.map((item) => (
              <ContentItem key={item.content_id} item={item} onDelete={deleteContent} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ContentItem({ item, onDelete }) {
  return (
    <div className="flex items-center justify-between p-3 border border-white/10 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-3">
        {item.type === 'video' ? (
          <Video className="w-4 h-4 text-gray-500" />
        ) : (
          <Image className="w-4 h-4 text-gray-500" />
        )}
        <div>
          <p className="text-white text-xs">{item.original_filename}</p>
          <p className="text-gray-600 text-[10px]">
            {(item.size / 1024 / 1024).toFixed(2)} MB · {new Date(item.uploaded_at).toLocaleDateString('el-GR')}
          </p>
        </div>
      </div>
      <button
        onClick={() => onDelete(item.content_id)}
        className="text-gray-600 hover:text-red-400 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

export default SignageModal;
